/* Warrior footer wordmark — grain-diffusion animation.
 *
 * Ported from the design prototype (index.html `mountGrainDiffusion`). The shader is
 * unchanged; what is new is that every parameter comes from data- attributes written by
 * the section, so a merchant tunes the animation in the theme editor rather than in code.
 *
 * A SEPARATE FILE, not appended to custom.js. custom.js is loaded on every page with
 * `defer`; this is ~4KB of WebGL that only the footer needs, and it is loaded lazily on
 * scroll (see the IntersectionObserver at the foot of this file). Putting it in custom.js
 * would make every page pay for a footer animation.
 *
 * THREE TIERS, deliberately:
 *   WebGL            -> the animation
 *   canvas, no WebGL -> the same text drawn flat in 2D
 *   neither, or no JS -> the real <p> in the markup, which we simply never hide
 *
 * The <p> is hidden only after the canvas has actually painted, so a failure at any
 * point leaves real text on screen rather than an empty box.
 */
(function () {
  var RM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function num(el, key, dflt) {
    var v = parseFloat(el.getAttribute('data-' + key));
    return isNaN(v) ? dflt : v;
  }

  function mount(cv) {
    var fallback = cv.parentNode.querySelector('[data-fw-fallback]');
    var P = {
      word: cv.getAttribute('data-word') || 'Outwork. Outlast.',
      fontSize: num(cv, 'font-size', 150),
      speed: num(cv, 'speed', 2),
      reveal: num(cv, 'reveal', 0.5),
      idle: num(cv, 'idle', 0.3),
      distort: num(cv, 'distort', 0.9),
      freqX: num(cv, 'freqx', 4),
      freqY: num(cv, 'freqy', 8),
      octaves: num(cv, 'octaves', 4),
      blur: num(cv, 'blur', 0.4),
      grain: num(cv, 'grain', 0.35),
      grainSize: num(cv, 'grain-size', 2),
      grainFps: num(cv, 'grain-fps', 24),
      loop: cv.getAttribute('data-loop') === 'true'
    };

    // `reveal` of 0 divides by zero in the progress calc and freezes the animation at
    // NaN. Clamp rather than trust the setting: the range allows 0.
    if (P.reveal <= 0) P.reveal = 0.01;

    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var baseFont = (P.fontSize * dpr) + 'px ROAM, Impact, sans-serif';
    var W = 1, H = 1, font = baseFont;

    var draw = function () {
      // MEASURED HERE, after fonts have loaded, not when mount() is called. The mobile
      // footer once measured the canvas while the fallback <p> still shared its flex row,
      // squeezing it to 216px of a 338px slot; the backing store was sized to that and then
      // stretched when the <p> hid ("loads at the wrong size", 2026-10-02). The <p> now sits
      // over the canvas (custom.css), and measuring late is the second guard.
      var rect = cv.getBoundingClientRect();
      W = Math.max(1, Math.round(rect.width * dpr));
      H = Math.max(1, Math.round(rect.height * dpr));
      cv.width = W;
      cv.height = H;

      // FIT THE TEXT TO THE CANVAS. The font-size setting is a MAXIMUM: at 150px the phrase
      // is ~850px wide, so on a 338px phone canvas only the middle ("RK. OUT") was visible.
      // Scale down until it fits 92% of the width and 70% of the height.
      var probe = document.createElement('canvas').getContext('2d');
      probe.font = baseFont;
      var tw = probe.measureText(P.word).width || 1;
      var px = P.fontSize * dpr;
      px = Math.min(px, px * (W * 0.92) / tw, H * 0.7);
      font = Math.max(8, Math.floor(px)) + 'px ROAM, Impact, sans-serif';

      var oc = document.createElement('canvas');
      oc.width = W;
      oc.height = H;
      var o = oc.getContext('2d');
      o.font = font;
      o.textAlign = 'center';
      o.textBaseline = 'middle';
      o.fillStyle = '#FFFFFF';
      o.fillText(P.word, W / 2, H / 2 + Math.round(parseFloat(font) * 0.05));

      var gl = null;
      try {
        gl = cv.getContext('webgl', { alpha: true, premultipliedAlpha: true });
      } catch (e) { gl = null; }

      // Tier 2: no WebGL. Draw the text flat and stop.
      if (!gl) {
        var c2 = cv.getContext('2d');
        if (c2) {
          c2.drawImage(oc, 0, 0);
          if (fallback) hide(fallback);
        }
        return;
      }

      var vs = 'attribute vec2 p;varying vec2 v;void main(){v=vec2(p.x*.5+.5,.5-p.y*.5);gl_Position=vec4(p,0.,1.);}';
      var fs = 'precision mediump float;varying vec2 v;uniform sampler2D t;' +
        'uniform float u_t,u_g,u_gs,u_gv,u_d,u_b,u_p,u_fx,u_fy,u_oct,u_idle,u_rx,u_ry;uniform vec3 u_col;' +
        'float h(vec2 q){return fract(sin(dot(q,vec2(127.1,311.7)))*43758.5453);}' +
        'float n2(vec2 q){vec2 i=floor(q),f=fract(q);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}' +
        'float fbm(vec2 q){float s=0.,a=.5;for(int i=0;i<6;i++){if(float(i)>=u_oct)break;s+=a*n2(q);q*=2.1;a*=.5;}return s;}' +
        'void main(){' +
        'float nz=fbm(v*vec2(u_fx,u_fy)+vec2(u_t*.18,u_t*.12));' +
        'vec2 uv=v+(nz-.5)*(u_d*mix(.28,.012+u_idle*.08,u_p));' +
        'float br=u_b*mix(.030,.002+u_idle*.006,u_p);' +
        'vec4 c=vec4(0.);' +
        'for(int x=-1;x<=1;x++){for(int y=-1;y<=1;y++){c+=texture2D(t,uv+vec2(float(x),float(y))*br);}}' +
        'c/=9.;c*=vec4(u_col,1.);' +
        'c*=smoothstep(nz*.7,nz*.7+.25,u_p*1.5);' +
        'float g=h(floor(v*vec2(u_rx,u_ry)/u_gs)+floor(u_t*u_gv));' +
        'c*=mix(1.,.3+.9*g,u_g);gl_FragColor=c;}';

      var mk = function (src, ty) {
        var sh = gl.createShader(ty);
        gl.shaderSource(sh, src);
        gl.compileShader(sh);
        return sh;
      };
      var pr = gl.createProgram();
      gl.attachShader(pr, mk(vs, gl.VERTEX_SHADER));
      gl.attachShader(pr, mk(fs, gl.FRAGMENT_SHADER));
      gl.linkProgram(pr);

      // If the program failed to link, fall back rather than render nothing. A silent
      // black box is the worst outcome and the easiest to ship by accident.
      if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) {
        var c2b = cv.getContext('2d');
        if (c2b) { c2b.drawImage(oc, 0, 0); if (fallback) hide(fallback); }
        return;
      }
      gl.useProgram(pr);

      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(pr, 'p');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

      var tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, oc);

      var U = function (n) { return gl.getUniformLocation(pr, n); };
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.viewport(0, 0, W, H);
      gl.uniform1f(U('u_g'), P.grain);
      gl.uniform1f(U('u_gs'), P.grainSize);
      gl.uniform1f(U('u_gv'), P.grainFps);
      gl.uniform1f(U('u_d'), P.distort);
      gl.uniform1f(U('u_b'), P.blur);
      gl.uniform1f(U('u_fx'), P.freqX);
      gl.uniform1f(U('u_fy'), P.freqY);
      gl.uniform1f(U('u_oct'), P.octaves);
      gl.uniform1f(U('u_idle'), P.idle);
      gl.uniform3fv(U('u_col'), [1, 1, 1]);
      gl.uniform1f(U('u_rx'), W);
      gl.uniform1f(U('u_ry'), H);

      var uT = U('u_t');
      var uP = U('u_p');
      var t0 = (window.performance || Date).now();
      var raf = 0;

      var tick = function (tm) {
        var el = (tm - t0) / 1000 * P.speed;
        // Reduced motion jumps straight to the finished frame: the phrase is legible
        // immediately and nothing moves.
        var p = RM ? 1 : (P.loop ? Math.min(1, (el % (P.reveal + 2.5)) / P.reveal)
                                 : Math.min(1, el / P.reveal));
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniform1f(uT, el);
        gl.uniform1f(uP, p);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        if (!RM || el < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      if (fallback) hide(fallback);

      // Let a resize re-mount cleanly: stop this loop and its observer first.
      cv._fwStop = function () {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        tick = function () {};
        if (cv._fwIO) cv._fwIO.disconnect();
      };

      // Stop drawing when the footer leaves the viewport. A looping animation running
      // off-screen is pure battery cost, and the footer is off-screen most of a session.
      if ('IntersectionObserver' in window) {
        cv._fwIO = new IntersectionObserver(function (es) {
          es.forEach(function (e) {
            if (e.isIntersecting) {
              if (!raf) raf = requestAnimationFrame(tick);
            } else if (raf) {
              cancelAnimationFrame(raf);
              raf = 0;
            }
          });
        }).observe(cv);
      }
    };

    // Wait for ROAM before drawing, or the text is rasterised in the fallback face and
    // the wrong glyph shapes are baked into the texture for the whole session.
    if (document.fonts && document.fonts.load) {
      document.fonts.load(baseFont).then(draw).catch(draw);
    } else {
      draw();
    }

    // RE-FIT ON RESIZE — rotation, the mobile URL bar, a desktop window drag. The backing
    // store is a fixed bitmap, so a box that changes size after mount is otherwise stretched.
    if ('ResizeObserver' in window && !cv._fwRO) {
      var lastW = Math.round(cv.getBoundingClientRect().width);
      var timer = 0;
      cv._fwRO = new ResizeObserver(function () {
        var w = Math.round(cv.getBoundingClientRect().width);
        if (Math.abs(w - lastW) < 2) return;   // height-only jitter from the URL bar
        lastW = w;
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (cv._fwStop) cv._fwStop();
          draw();
        }, 150);
      });
      cv._fwRO.observe(cv);
    }
  }

  function hide(el) {
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-fw-canvas]');
    Array.prototype.forEach.call(nodes, function (cv) {
      if (cv.getAttribute('data-fw-mounted')) return;
      // Lazy: only build the WebGL context once the footer is near the viewport.
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (es, ob) {
          if (es.some(function (e) { return e.isIntersecting; })) {
            ob.disconnect();
            cv.setAttribute('data-fw-mounted', '1');
            mount(cv);
          }
        }, { rootMargin: '128px' });
        io.observe(cv);
      } else {
        cv.setAttribute('data-fw-mounted', '1');
        mount(cv);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor: re-mount when the section is re-rendered.
  document.addEventListener('shopify:section:load', boot);
})();
